import React, { useMemo } from 'react'

import {
  ProTableOptions,
  MRT_SortingState,
  ProTable
} from '@tidbcloud/uikit/biz'

import { eventTracking } from '~/utils/telemetry'

import { useStatementContext } from '../utils/context'
import { FEATURE_NAME } from '../utils/helpers'
import { StatementModel } from '../utils/type'
import { useStatementColumns } from './columns'
import { useStatementListUrlState } from './url-state'

interface ListTableProps<T> {
  data: T[]
  loading?: boolean

  sortBy?: MRT_SortingState
  onSortChange?: ProTableOptions['onSortingChange']
}

export const ListTable: React.FC<
  React.PropsWithChildren<ListTableProps<StatementModel>>
> = ({ data, loading, sortBy = [], onSortChange }) => {
  const ctx = useStatementContext()

  const { pagination, setPagination } = useStatementListUrlState()

  const columns = useStatementColumns(data, ctx.cfg.computed)

  const sortedData = useMemo(() => {
    if (!data || !sortBy[0]) {
      return data
    }
    const [{ id, desc }] = sortBy
    const sorted = [...data]
    sorted.sort((a, b) => {
      const aVal = a[id as keyof StatementModel] ?? 0
      const bVal = b[id as keyof StatementModel] ?? 0
      if (desc) {
        return aVal > bVal ? -1 : 1
      } else {
        return aVal > bVal ? 1 : -1
      }
    })
    return sorted
  }, [data, sortBy])

  const finalData = useMemo(() => {
    const { curPage, pageSize } = pagination
    return sortedData.slice((curPage - 1) * pageSize, curPage * pageSize)
  }, [sortedData, pagination])

  return (
    <ProTable<StatementModel>
      emptyMessage='No Data'
      enableSorting
      manualSorting
      enableColumnPinning
      sortDescFirst
      layoutMode='grid'
      enableColumnResizing
      columns={columns}
      onSortingChange={onSortChange}
      data={finalData}
      state={{ sorting: sortBy, isLoading: loading }}
      initialState={{ columnPinning: { left: ['digest_text'] } }}
      mantinePaperProps={{ mx: 24 }}
      mantineTableBodyRowProps={({ row }) =>
        row.original.digest_text
          ? {
              className: 'cursor-pointer',
              onClick: () => {
                if (loading) {
                  return
                }
                ctx.event.gotoDetail({
                  digest: row.original.digest ?? '',
                  schema_name: row.original.schema_name ?? ''
                })
                eventTracking(`${FEATURE_NAME} List Table Row Clicked`)
              }
            }
          : {}
      }
      manualPagination={true}
      pagination={{
        value: pagination.curPage,
        total: Math.ceil((data?.length ?? 0) / pagination.pageSize),
        onChange: (v) => setPagination({ ...pagination, curPage: v })
      }}
    />
  )
}
